import React, { ReactNode } from "react";
import { cn } from "@/lib/utils";
import TableSkeleton from "./TableSkeleton";
import TableEmpty from "./TableEmpty";

interface DataTableWrapperProps {
  children: ReactNode;
  isLoading?: boolean;
  isEmpty?: boolean;
  emptyState?: {
    title: string;
    description?: string;
    action?: ReactNode;
    icon?: ReactNode;
  };
  skeletonRows?: number;
  skeletonColumns?: number;
  className?: string;
}

/**
 * DataTableWrapper - Responsive table container with loading and empty states
 * 
 * Provides:
 * - Horizontal scroll on mobile (prevents table breaking)
 * - Skeleton loading state
 * - Configurable empty state with call-to-action
 * - Thin styled scrollbar
 * 
 * @example
 * <DataTableWrapper
 *   isLoading={isLoading}
 *   isEmpty={data.length === 0}
 *   emptyState={{
 *     title: "No estimates yet",
 *     description: "Create your first estimate to get started",
 *     action: <Button>Create Estimate</Button>
 *   }}
 * >
 *   <Table>...</Table>
 * </DataTableWrapper>
 */
const DataTableWrapper = ({
  children,
  isLoading = false,
  isEmpty = false,
  emptyState,
  skeletonRows = 5,
  skeletonColumns = 5,
  className,
}: DataTableWrapperProps) => {
  // Loading state
  if (isLoading) {
    return (
      <div className={cn("w-full", className)}>
        <TableSkeleton rows={skeletonRows} columns={skeletonColumns} />
      </div>
    );
  }

  // Empty state
  if (isEmpty && emptyState) {
    return (
      <div className={cn("w-full", className)}>
        <TableEmpty
          title={emptyState.title}
          description={emptyState.description}
          action={emptyState.action}
          icon={emptyState.icon}
        />
      </div>
    );
  }

  // Content with horizontal scroll wrapper
  return (
    <div
      className={cn(
        "w-full overflow-x-auto",
        // Custom scrollbar styles (defined in index.css)
        className
      )}
    >
      <div className="min-w-full">{children}</div>
    </div>
  );
};

export default DataTableWrapper;
